// Local symbol indexes for ticker autocomplete, one per market.
//
// US: Finnhub's /search only matches whole symbols or words of the company name ("BRK" finds neither BRK.A nor
// BRKL, verified 2026-09-16), so prefix search runs here over /stock/symbol?exchange=US (free tier, one call).
// Canada: Twelve Data's /stocks and /etfs listings for TSX, TSX Venture, CSE and Cboe Canada, no key needed.
// Both are cached in IndexedDB for a week; the US one also keeps keystrokes off Finnhub's 60 calls/min.
import { finnhubKey, settingsStore } from '@/settings/settingsStore';
import { appStore } from '@/state/appStore';
import { repo } from '@/storage/db';
import type { Market, SymbolListing } from '@/types';
import { finnhubSymbols, type SymbolHit } from './finnhub';
import { twelvedataListings } from './twelvedata';

const KEYS: Record<Market, string> = { US: 'finnhub:US', CA: 'twelvedata:CA' };
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
const RETRY_MS = 5 * 60 * 1000;
/** Listed venues rank above OTC / junior boards within a match group. */
const MAJOR_MICS = new Set(['XNAS', 'XNMS', 'XNGS', 'XNCM', 'XNYS', 'ARCX', 'BATS', 'XASE', 'IEXG', 'XTSE', 'NEOE']);

interface Entry extends SymbolListing { market: Market; upperName: string; major: boolean }
interface IndexState { entries: Entry[] | null; loading: Promise<void> | null; lastAttempt: number }

const state: Record<Market, IndexState> = { US: { entries: null, loading: null, lastAttempt: 0 }, CA: { entries: null, loading: null, lastAttempt: 0 } };

const CA_PREF = ['XTSE', 'NEOE', 'XTSX', 'XCNQ'];
const CA_DR_PREF = ['NEOE', 'XTSE', 'XTSX', 'XCNQ'];

/**
 * Twelve Data lists a security once per venue, and CDRs trade on both Cboe Canada and the TSX. Keep one row per
 * symbol, preferring the TSX for ordinary listings and Cboe Canada (the CDR home venue) for depositary receipts.
 * Pure; exported for tests.
 */
export function dedupeCanadian(rows: SymbolListing[]): SymbolListing[] {
  const rank = (r: SymbolListing) => {
    const i = (/depositary/i.test(r.type) ? CA_DR_PREF : CA_PREF).indexOf(r.mic);
    return i < 0 ? 99 : i;
  };
  const best = new Map<string, SymbolListing>();
  for (const r of rows) {
    const sym = r.symbol.toUpperCase();
    const cur = best.get(sym);
    if (!cur || rank(r) < rank(cur)) best.set(sym, r);
  }
  return [...best.values()];
}

function build(rows: SymbolListing[], market: Market): Entry[] {
  const seen = new Set<string>();
  const out: Entry[] = [];
  for (const r of market === 'CA' ? dedupeCanadian(rows) : rows) {
    const symbol = r.symbol.toUpperCase();
    if (seen.has(symbol)) continue;
    seen.add(symbol);
    out.push({ ...r, symbol, market, upperName: r.name.toUpperCase(), major: MAJOR_MICS.has(r.mic) });
  }
  return out;
}

/** Replaces one market's in-memory index (exported for tests; ensureSymbolIndex uses it internally). */
export function setSymbolIndexRows(rows: SymbolListing[], market: Market = 'US'): void {
  state[market].entries = build(rows, market);
}

export function symbolIndexReady(market: Market = 'US'): boolean {
  return state[market].entries !== null;
}

/**
 * Loads a market's cached listing, refreshing when missing or older than TTL. The US index needs a Finnhub key
 * (nothing happens without one); the Canadian one is public. Never throws; safe to call on every keystroke.
 */
export function ensureSymbolIndex(market: Market, apiKey?: string): Promise<void> {
  const st = state[market];
  if (st.loading) return st.loading;
  if (market === 'US' && !apiKey) return Promise.resolve();
  st.loading = (async () => {
    let cached;
    try {
      cached = await repo.getSymbolIndex(KEYS[market]);
    } catch {
      cached = undefined;
    }
    if (cached && !st.entries) st.entries = build(cached.rows, market);
    if (cached && Date.now() - cached.fetchedAt < TTL_MS) return;
    if (Date.now() - st.lastAttempt < RETRY_MS) return;
    st.lastAttempt = Date.now();
    try {
      const rows = market === 'US' ? await finnhubSymbols(apiKey!) : await twelvedataListings();
      if (rows.length === 0) return;
      st.entries = build(rows, market);
      try {
        await repo.putSymbolIndex({ key: KEYS[market], fetchedAt: Date.now(), rows });
      } catch {
        // Cache write failed (quota / private mode): the in-memory index still serves this session.
      }
    } catch {
      // Network or key error: keep whatever we have; the remote search fallback still works.
    }
  })().finally(() => {
    st.loading = null;
  });
  return st.loading;
}

/** Starts loading in the background: the US index when a Finnhub key exists, the Canadian one when any holding is Canadian. */
export function warmSymbolIndex(): void {
  const key = finnhubKey(settingsStore.get());
  if (key) void ensureSymbolIndex('US', key);
  if (appStore.get().assets.some((a) => a.market === 'CA')) void ensureSymbolIndex('CA');
}

/**
 * Prefix search: symbol prefix first, then a name word starting with the query, then any name substring
 * (3+ chars). Listed venues precede OTC within each group; search.ts applies the final relevance ranking.
 */
export function searchSymbolIndex(query: string, market: Market = 'US', limit = 30): SymbolHit[] {
  const list = state[market].entries;
  if (!list) return [];
  const q = query.trim().toUpperCase();
  if (!q) return [];
  const sym: Entry[] = [];
  const word: Entry[] = [];
  const sub: Entry[] = [];
  for (const e of list) {
    if (e.symbol.startsWith(q)) sym.push(e);
    else if (q.length >= 2 && (e.upperName.startsWith(q) || e.upperName.includes(` ${q}`))) word.push(e);
    else if (q.length >= 3 && e.upperName.includes(q)) sub.push(e);
  }
  const order = (a: Entry, b: Entry) => Number(b.major) - Number(a.major) || a.symbol.length - b.symbol.length || a.symbol.localeCompare(b.symbol);
  return [...sym.sort(order), ...word.sort(order), ...sub.sort(order)]
    .slice(0, limit)
    .map((e) => ({ symbol: e.symbol, name: e.name, type: e.type, exchange: e.mic || undefined, mic: e.mic || undefined, market: e.market, currency: e.currency }));
}
